/* eslint sonarjs/cognitive-complexity: 0 */

import React, { FC, Fragment, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { StylesProvider, CssBaseline, createGenerateClassName, jssPreset, MuiThemeProvider } from '@material-ui/core';
import styled, { StyleSheetManager, css } from 'styled-components';
import rtlPlugin from 'stylis-plugin-rtl';
import rtl from 'jss-rtl';
import { create } from 'jss';
import { palette, Theme } from '@dev/ee-components';
import { SetRtl } from '@/store/config/actions';
import i18n, { languagesWithRtl } from '../../../i18n';

const overideFontFamilies = {
    ur: css`
        line-height: normal;
    `,
};

const FontFamilyOverride = styled.div<{ $language: string }>`
    ${({ $language }) =>
        overideFontFamilies[$language] &&
        `
        * {
            ${overideFontFamilies[$language]}
        }
    `}
`;

const ClearOldDashStyles = styled.div`
    input,
    input[type='number'] {
        padding: 0;
        border: 0;

        &:disabled {
            color: #cbd5e0;
            background: transparent;
        }
        &:focus {
            border: none;
            padding: 0;
            background: transparent;
        }
        &[readonly] {
            color: inherit;
            background: transparent;
            border: none;
            cursor: default;
        }
    }
    input[type='file'] {
        display: none;
    }
    div[disabled],
    textarea[readonly] {
        background-color: inherit;
    }
    label {
        display: inline-flex;
    }
    .logo + div > label {
        margin-bottom: 0px;
    }
    & .MuiSvgIcon-root {
        width: 20px;
        height: 20px;
    }

    & > .MuiContainer-root {
        padding: 0px 0px 50px 0px;
        ${Theme.breakpoints.up('sm')} {
            padding: 0;
        }

        & .MuiTypography-h1 {
            line-height: 44px;
        }
    }
    & .MuiDialogContent-root .dialog-paragraph .MuiAlert-standardInfo {
        padding: 6px 16px;
        background: ${palette.indigo[1]};
    }
    & .header--name {
        word-break: break-all;
    }
`;

interface Props {
    children: React.ReactNode;
    standalone?: boolean;
}

//NOTE: need languages with rtl

const jss = create({
    plugins: [...jssPreset().plugins, rtl()] as any[],
    insertionPoint: 'jss-insertion-point',
});

const removeChildren = (items: NodeListOf<HTMLStyleElement> | NodeListOf<Element>): void => {
    for (let i = 0; i < items?.length; i++) {
        items[i]?.parentNode?.removeChild(items[i]);
    }
};

export const Styles: FC<Props> = (props: Props): JSX.Element => {
    const dispatch = useDispatch();
    const isRtlOnHTMLTag = document.getElementsByTagName('html')?.[0]?.getAttribute('dir') === 'rtl' || false;
    const isLocaleRtl = languagesWithRtl?.includes(localStorage.getItem('lng'));

    /**
     * Layout Effect to set trl to themes and styles provider.
     * In the cleanup function grab all styles under jss-insertion-point(where all styles from material sits) and delete them, also deleting google font, as the  nextGenInit() func. loads it again whenever it starts.
     * use it only without standalone
     */

    useLayoutEffect((): any => {
        dispatch(SetRtl(isLocaleRtl));
        document.body.setAttribute('dir', isLocaleRtl || isRtlOnHTMLTag ? 'rtl' : 'ltr');
        (window as any).intercomSettings = {
            ...(window as any).intercomSettings,
            alignment: isLocaleRtl || isRtlOnHTMLTag ? 'left' : 'right',
        };
        return (): any => {
            if (!props.standalone) {
                removeChildren(document.head.querySelectorAll('link[href*="googleapis"]'));
                removeChildren(document.head.querySelectorAll('style'));
            }
        };
    }, [isRtlOnHTMLTag, isLocaleRtl]);

    const generateClassName = createGenerateClassName({
        productionPrefix: 'eem',
    });

    const customTheme = {
        ...Theme,
        props: {
            ...Theme.props,
            MuiDialog: {
                disablePortal: true,
            },
        },
        direction: isLocaleRtl || isRtlOnHTMLTag ? 'rtl' : 'ltr',
    };

    const pluginsForStyledComponents = (): any[] => {
        const plugins = [];
        if (isLocaleRtl || isRtlOnHTMLTag) {
            plugins.push(rtlPlugin);
        }
        return plugins;
    };

    const OldStylesOverride = props.standalone ? Fragment : ClearOldDashStyles;

    return (
        <StyleSheetManager stylisPlugins={pluginsForStyledComponents as any}>
            <StylesProvider jss={jss as any} generateClassName={generateClassName}>
                <CssBaseline />
                <FontFamilyOverride $language={i18n.language}>
                    <OldStylesOverride>
                        <MuiThemeProvider theme={customTheme}>{props.children}</MuiThemeProvider>
                    </OldStylesOverride>
                </FontFamilyOverride>
            </StylesProvider>
        </StyleSheetManager>
    );
};
